import { api } from './api.ts';

export interface Friend {
    id: string;
    displayName: string;
    email: string;
    avatarUrl: string | null;
    friendsSince: string;
    /** Parties de putting jouées ensemble. */
    gamesTogether: number;
    /** Son index, ou `null` s'il ne l'a jamais renseigné. */
    handicapIndex: number | null;
}

export interface InvitePreview {
    displayName: string;
    avatarUrl: string | null;
    expiresAt: string;
    isMine: boolean;
    alreadyFriends: boolean;
}

/** Ce qu'un joueur a fait dans un mode : ses victoires, sur combien de
 *  parties. Les deux vont ensemble — trois victoires sur trois et trois sur
 *  douze ne se lisent pas pareil. */
export interface BilanMode {
    wins: number;
    games: number;
}

/** Une ligne du podium des partenaires. Le viewer en fait partie : c'est le
 *  classement de vos parties COMMUNES, et il y a forcément joué. */
export interface PartenaireClasse {
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    rounds: BilanMode;
    putting: BilanMode;
}

/** Les deux modes arrivent ensemble : changer de filtre à l'écran ne doit pas
 *  attendre le réseau d'un practice. */
export const getPodiumPartenaires = () =>
    api<{ players: PartenaireClasse[] }>('/friends/podium').then((r) => r.players);

export const listFriends = () =>
    api<{ friends: Friend[] }>('/friends').then((r) => r.friends);

/** Le jeton n'est lisible qu'au moment où il est créé : la base n'en garde que
 *  l'empreinte. Un lien perdu se remplace, il ne se retrouve pas. */
export const createInvite = () =>
    api<{ token: string; expiresAt: string }>('/friends/invites', { method: 'POST' });

export const previewInvite = (token: string) =>
    api<{ invite: InvitePreview }>(`/friends/invites/${token}`).then((r) => r.invite);

export const acceptInvite = (token: string) =>
    api<{ friend: { id: string; displayName: string; avatarUrl: string | null } }>(
        `/friends/invites/${token}/accept`, { method: 'POST' },
    );

export const removeFriend = (id: string) =>
    api<void>(`/friends/${id}`, { method: 'DELETE' });

/** Les liens encore valables que j'ai créés. Le jeton n'y figure pas : la base
 *  n'en garde que l'empreinte. On voit qu'un lien court, pas lequel. */
export const listInvites = () =>
    api<{ invites: { createdAt: string; expiresAt: string }[] }>('/friends/invites')
        .then((r) => r.invites);

export const revokeInvites = () =>
    api<{ revoked: number }>('/friends/invites', { method: 'DELETE' });

/** L'adresse complète à partager.
 *
 *  Construite depuis window.location plutôt que depuis une variable de build :
 *  l'application tourne sur golf.fevio.fr, sur golfs.fevio.fr et en local, et
 *  un lien figé à la compilation enverrait les invités sur le mauvais
 *  environnement. */
export const inviteUrl = (token: string): string =>
    `${window.location.origin}/amis/invitation/${token}`;

// -- Le profil d'un partenaire ----------------------------------------------

export interface FriendRound {
    id: string;
    startedAt: string;
    courseName: string;
    totalStrokes: number;
    holesPlayed: number;
    teeLabel: string | null;
    holes: { number: number; par: number | null; strokes: number | null }[];
}

export interface FriendProfile {
    friend: {
        id: string;
        displayName: string;
        email: string | null;
        avatarUrl: string | null;
        friendsSince: string;
        handicapIndex: number | null;
        gamesTogether: number;
        roundsTogether: number;
    };
    /** Les trois derniers tours termines. On regarde ou en est un partenaire,
     *  on ne fouille pas son annee. */
    rounds: FriendRound[];
    handicaps: { indexValue: number; recordedAt: string }[];
}

export const getFriendProfile = (id: string) => api<FriendProfile>(`/friends/${id}/profil`);
